// Columns of the ShareJet bookings table (IDs, user, aircraft, route, departure, seats, price, status,
// removable ad-hoc labels, MGAV progress).
import { Progress, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { WarningOutlined } from '@ant-design/icons';

import type { AdminBookingRow, AircraftCategory } from '../../api/types';
import { CATEGORY_LABELS } from '../../api/types';
import { ADHOC_COLORS, AdhocTag } from '../../components/AdhocTag';
import { BOOKING_STATUS_COLORS, BOOKING_STATUS_LABELS, statusLabel } from '../../data/statuses';
import { mgavPercent } from '../../data/mgav';
import { gbp } from '../../data/format';
import { withAbbr } from '../../data/airportTime';

const { Text } = Typography;

// Map the raw category enum to a human label, falling back to the raw string.
function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category as AircraftCategory] ?? category ?? '—';
}

export function bookingColumns({
  removeAdhoc,
}: {
  removeAdhoc: {
    isPending: boolean;
    mutate: (v: {
      id: string;
      body: {
        dismissMgavOverdue?: boolean;
        clearNewSeats?: boolean;
        dismissPassportNeeded?: boolean;
        dismissPaymentRequired?: boolean;
      };
    }) => void;
  };
}): ColumnsType<AdminBookingRow> {
  return [
    // 1. Booking ID — short form, monospaced.
    {
      title: 'Booking ID',
      dataIndex: 'id',
      key: 'id',
      width: 110,
      fixed: 'left',
      render: (id: string) => (
        <Text code style={{ fontSize: 12 }}>{id.slice(-8)}</Text>
      ),
    },
    // 2. Flight ID.
    {
      title: 'Flight ID',
      dataIndex: 'flightNumber',
      key: 'flightNumber',
      width: 110,
      render: (v: string | null) => v || '—',
    },
    // SHAR-1074 — the customer, as on the Charter bookings list: name (email).
    {
      title: 'User',
      key: 'user',
      width: 230,
      ellipsis: { showTitle: true },
      render: (_, r) => (r.user ? `${r.user.name || '—'} (${r.user.email})` : '—'),
    },
    // 3. Tail number.
    {
      title: 'Tail number',
      dataIndex: 'tailNumber',
      key: 'tailNumber',
      width: 120,
      render: (v: string | null) => v || '—',
    },
    // 4. Aircraft model.
    {
      title: 'Aircraft model',
      dataIndex: 'modelName',
      key: 'modelName',
      width: 160,
      ellipsis: { showTitle: true },
      render: (v: string | null) => v || '—',
    },
    // 5. Aircraft category.
    {
      title: 'Category',
      key: 'category',
      width: 150,
      ellipsis: { showTitle: true },
      render: (_, r) => categoryLabel(r.category),
    },
    // 6. Operator.
    {
      title: 'Operator',
      key: 'operator',
      width: 160,
      ellipsis: { showTitle: true },
      render: (_, r) => r.operator || '—',
    },
    // 7. Route — ICAO codes.
    {
      title: 'Route',
      key: 'route',
      width: 150,
      render: (_, r) => (
        <Text strong>{r.originIcao} → {r.destIcao}</Text>
      ),
    },
    // 8. Departure date/time.
    {
      title: 'Departure',
      key: 'departure',
      width: 140,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {/* SHAR-1192 — the origin airport's clock, with its zone. */}
          <Text>{r.departureZoned?.localDate ?? r.departureDate}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{withAbbr(r.departureZoned?.localTime ?? r.departureTime, r.departureZoned)}</Text>
        </Space>
      ),
    },
    // 9. Seats.
    {
      title: 'Seats',
      key: 'seats',
      width: 90,
      render: (_, r) => `${r.seatsBooked} / ${r.seatsTotal}`,
    },
    // 10. Price — per-seat + total.
    {
      title: 'Price',
      key: 'price',
      width: 140,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{gbp(r.seatPriceGbp)}/seat</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {gbp(r.totalGbp)} total
          </Text>
        </Space>
      ),
    },
    // 11. Status.
    {
      title: 'Status',
      key: 'status',
      // Regression 29 Sep - 130 left "Gathering passengers" (a 142px tag)
      // running under the Ad-hoc column; 180 holds it on one line.
      width: 180,
      render: (_, r) => (
        <Tag color={BOOKING_STATUS_COLORS[r.status]}>
          {statusLabel(BOOKING_STATUS_LABELS, r.status, r.statusLabel)}
        </Tag>
      ),
    },
    // 12. Ad-hoc status — MGAV-overdue / New-seats tags (with remove popups).
    {
      title: 'Ad-hoc status',
      key: 'adhoc',
      width: 200,
      // Stop clicks inside this cell (the ad-hoc tags / popconfirms)
      // from triggering the row-click navigation.
      onCell: () => ({
        onClick: (e) => e.stopPropagation(),
        style: { cursor: 'default' },
      }),
      render: (_, r) => (
        <Space size={4} wrap>
          {!r.mgavOverdue && !r.newSeats && !r.passportNeeded && !r.notPaid ? (
            <Text type="secondary">—</Text>
          ) : null}
          {/* SHAR-163 — computed. SHAR-1088 — dismissable like the others; it
              comes back when another pre-auth lapses. */}
          {r.notPaid ? (
            <AdhocTag
              color="red"
              label="Payment required"
              title="Remove label"
              description="Remove the “Payment required” label from this booking? It comes back if another pre-authorisation expires."
              loading={removeAdhoc.isPending}
              onRemove={() =>
                removeAdhoc.mutate({ id: r.id, body: { dismissPaymentRequired: true } })
              }
            />
          ) : null}
          {/* SHAR-452 — "Passport needed" is now dismissable via a pop-up,
              like MGAV overdue / New seats booked. */}
          {r.passportNeeded ? (
            <AdhocTag
              color={ADHOC_COLORS.passportNeededList}
              label="Passport needed"
              title="Remove label"
              description="Remove the “Passport needed” label from this booking?"
              loading={removeAdhoc.isPending}
              onRemove={() =>
                removeAdhoc.mutate({ id: r.id, body: { dismissPassportNeeded: true } })
              }
            />
          ) : null}
          {r.mgavOverdue ? (
            <AdhocTag
              color="volcano"
              icon={<WarningOutlined />}
              label="MGAV overdue"
              title="Remove label"
              description="Remove the “MGAV overdue” label from this booking?"
              loading={removeAdhoc.isPending}
              onRemove={() =>
                removeAdhoc.mutate({ id: r.id, body: { dismissMgavOverdue: true } })
              }
            />
          ) : null}
          {r.newSeats ? (
            <AdhocTag
              color="gold"
              label="New seats booked"
              title="Remove label"
              description="Remove the “New seats booked” label from this booking?"
              loading={removeAdhoc.isPending}
              onRemove={() =>
                removeAdhoc.mutate({ id: r.id, body: { clearNewSeats: true } })
              }
            />
          ) : null}
        </Space>
      ),
    },
    // 13. MGAV progress — compact Progress bar.
    {
      title: 'MGAV progress',
      key: 'mgavProgress',
      width: 160,
      render: (_, r) => {
        const pct = mgavPercent(r.mgavProgress);
        if (pct == null) return <Text type="secondary">—</Text>;
        return (
          <Progress
            percent={pct}
            size="small"
            status={
              r.mgavOverdue
                ? 'exception'
                : pct >= 100
                  ? 'success'
                  : 'active'
            }
          />
        );
      },
    },
  ];
}
